import { observer } from "mobx-react-lite";
import { Navigate, useParams } from "react-router-dom";

import { useAuthStore } from "@/hooks/useAuthStore";
import { useBuildingScope } from "@/hooks/buildings/useBuildingScope";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { SigningContent } from "@/domains/signing/components/SigningContent";
import { SigningPageHeader } from "@/domains/signing/components/SigningPageHeader";
import { shouldLeaveSigningPage, useSigningVisible } from "@/domains/signing/useSigningAccess";
import { useSigningItems } from "@/domains/signing/useSigningItems";
import { useSigningPageState } from "@/domains/signing/useSigningPageState";

/**
 * Signing — one project's signed .asice containers plus every signing chain the
 * viewer can see in it: the ones they sent, the ones they were invited to, and
 * the ones they manage. The selected row shows the signer stepper, the source
 * files, and the actions the viewer may take (sign your turn, cancel as sender).
 *
 * PROJECT-SCOPED, not building-scoped. A chain is started from a project's
 * files and its container is filed back into that project
 * (`signing_sessions.project_id` / `documents.project_id`), so the tab lives in
 * Projects view and the list narrows to `useBuildingScope`'s selected project.
 *
 * Plus the building's UNFILED rows (`project_id IS NULL`), labelled as such.
 * They have no project page to live on, and a Projects-only page that simply
 * dropped them would make an unfiled chain uncompletable and an unfiled
 * container undownloadable. Two things produce them: containers signed before
 * FDP `20260731103000` (most recovered by its backfill), and — permanently — a
 * signing started from a building-root folder, which `dokobit-sign-start`
 * resolves to a null target quite legitimately.
 *
 * What the page still does NOT show: per-method Sign buttons. Dokobit's hosted
 * iframe owns the Smart-ID / Mobile-ID / ID-card choice, so "Sign now" opens
 * that iframe rather than replacing it.
 */
const Signing = observer(function Signing() {
  const { id } = useParams<{ id: string }>();
  const signingVisible = useSigningVisible();
  const { isReady, currentOrganization } = useOrganizationContext();
  const authStore = useAuthStore();

  const parsed = Number(id);
  const buildingId = Number.isFinite(parsed) ? parsed : null;

  // The one scope mechanism the building shell already uses — never a second
  // one. In Projects view this is the picked project; BuildingLayout gates the
  // Outlet until one is picked, so a null here in practice only means the shell
  // is still resolving (or, in the exempted case, is not gating this route).
  const { selectedProjectId } = useBuildingScope(id ?? "");

  const { items, isLoading, error, isTruncated } = useSigningItems(buildingId, selectedProjectId);
  const state = useSigningPageState(items);

  // A non-numeric :id can't be a building — bounce to the list instead of
  // rendering a convincing "nothing signed yet" for a building that doesn't
  // exist (the queries never fire with a null buildingId).
  if (buildingId == null) {
    return <Navigate to="/buildings" replace />;
  }

  // Same gate as the sidebar tab (BuildingLayout), plus the one thing a hidden
  // tab can't express: an org switched off mid-chain. Nothing server-side stops
  // an open chain when the flag flips, so bouncing a viewer who still has rows
  // would strand the invited signer's turn and the sender's Cancel until expiry
  // (see shouldLeaveSigningPage). Deliberately the org rollout flag only, NOT
  // ALLOW_SIGNING: an invited signer needs no permission to start a signing in
  // order to finish one they were invited to. `isReady` alone isn't enough: it
  // flips only after the FIRST org's RBAC has loaded and stays true across an
  // org switch, whose permission fetch is fired without being awaited.
  const rbacSettled =
    !currentOrganization || authStore.hasLoadedPermissions(currentOrganization.id);
  const leave = shouldLeaveSigningPage({
    visible: signingVisible,
    isLoading,
    itemCount: items.length,
  });
  if (isReady && rbacSettled && leave) {
    return <Navigate to={`/buildings/${id}`} replace />;
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <SigningPageHeader state={state} />
      <SigningContent
        buildingId={buildingId}
        isLoading={isLoading}
        error={error}
        isTruncated={isTruncated}
        state={state}
      />
    </div>
  );
});

export default Signing;
